import React from 'react';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  EmptyState,
  Flex,
  Heading,
  LoadingOrbit,
  Text,
} from '@chatsdet/ui';
import { WORKFLOW_HEALTH_LABEL } from '@chatsdet/types';
import { CheckCircle, Warning } from '@phosphor-icons/react';
import { useNavigate } from '@tanstack/react-router';
import { toUrlId } from '../../../lib/prefixed-ids';
import { useWorkflowsQuery } from '../../../hooks/useWorkflows';
import { workflowHealthBadgeVariants } from '../../components/workflows/workflowHealthBadge';

/** Reliability states worth surfacing, worst-first. Mirrors WorkflowHealth. */
const ATTENTION_HEALTH = ['failing', 'degraded', 'flaky', 'never_passed'];

export function FlowsNeedingAttention({ className }: { className?: string }) {
  const navigate = useNavigate();
  const query = useWorkflowsQuery({
    health: ATTENTION_HEALTH,
    flowType: ['standard'],
    pageSize: 6,
    sortBy: 'lastRunAt',
    sortOrder: 'desc',
  });

  const items = query.data.items;

  let body: React.ReactNode;
  if (query.isLoading) {
    body = (
      <Flex align="center" justify="center" className="py-8">
        <LoadingOrbit size="lg" />
      </Flex>
    );
  } else if (query.isError) {
    body = (
      <Flex direction="column" gap="md" align="start" className="py-2">
        <Text size="sm" className="text-muted-foreground">
          Unable to load flows right now.
        </Text>
        <Button variant="outline" onPress={() => query.refetch()}>
          Retry
        </Button>
      </Flex>
    );
  } else if (items.length === 0) {
    body = (
      <EmptyState
        icon={<CheckCircle size={28} />}
        title="All flows healthy"
        description="No failing or flaky flows right now."
        size="sm"
      />
    );
  } else {
    body = (
      <div className="flex flex-col">
        {items.map((flow) => {
          return (
            <button
              key={flow.id}
              type="button"
              onClick={() =>
                navigate({ to: '/flows/$workflowId', params: { workflowId: toUrlId('wf', flow.id) } })
              }
              className="-mx-2 flex w-[calc(100%+1rem)] items-center justify-between gap-3 rounded-lg px-2 py-2 text-left transition-colors hover:bg-muted/60"
            >
              <Text size="sm" weight="medium" className="min-w-0 truncate text-foreground">
                {flow.name}
              </Text>
              <Badge variant={workflowHealthBadgeVariants[flow.health]} className="shrink-0">
                {WORKFLOW_HEALTH_LABEL[flow.health]}
              </Badge>
            </button>
          );
        })}
      </div>
    );
  }

  return (
    <Card padding="lg" className={className}>
      <CardHeader>
        <Flex align="center" justify="between">
          <Flex align="center" gap="sm">
            <Warning size={18} />
            <Heading level={4}>Flows needing attention</Heading>
          </Flex>
          {items.length > 0 && (
            <Button variant="ghost" size="sm" onPress={() => navigate({ to: '/flows' })}>
              View all
            </Button>
          )}
        </Flex>
      </CardHeader>
      <CardContent>{body}</CardContent>
    </Card>
  );
}
